import { useCallback, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcTradereportingV1BlueSheetQueryServiceBlueSheetGet } from '@interxfi/api-client/generated/hooks/ixc-tradereporting-v1-blue-sheet-query-service-blue-sheet-get';
import { useIxfReportsV1ReportCommandServiceReportDownload } from '@interxfi/api-client/generated/hooks/ixf-reports-v1-report-command-service-report-download';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { Typography } from '@interxfi/design-system/components/typography';
import DownloadIcon from '@interxfi/design-system/icons/react/icon-download';
import { type IxcTradereportingV1BlueSheetState, IxcTradereportingV1BlueSheetStatus } from '@interxfi/types/generated/api';
import { downloadFromUrl } from '../../../../../../../../../../features/document/utils';
import { OrderSidePill } from '../../../../../../../../../../features/order/order-side-pill';
import {
  TradeReportingBlueSheetEventDrawer,
  TradeReportingBlueSheetEventSummary,
} from '../../../../../../../../../../features/trade-reporting/blue-sheet/trade-reporting-blue-sheet-event';
import { getTradeReportingBlueSheetStateListItems } from '../../../../../../../../../../features/trade-reporting/blue-sheet/trade-reporting-blue-sheet-list-items';
import { getTradeReportingBluesheetDetailPath } from '../../../../../../../../../../features/trade-reporting/blue-sheet/trade-reporting-blue-sheet-routing';
import { TradeReportingBlueSheetStatusPill } from '../../../../../../../../../../features/trade-reporting/blue-sheet/trade-reporting-blue-sheet-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/compliance/_compliance/reporting/blue-sheet/_blue-sheet/$reportId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `blue-sheet-${params.reportId}`,
        label: 'Blue Sheet',
        to: getTradeReportingBluesheetDetailPath(params.reportId),
        suffix: (
          <CopyButton content={params.reportId} label="Copy Blue Sheet ID" tooltip={{ copy: params.reportId, copied: 'Copied Blue Sheet ID' }} />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Blue Sheet Report'),
  component: BlueSheetReportDetail,
});

function getListItems(data: IxcTradereportingV1BlueSheetState | undefined) {
  const listItems = getTradeReportingBlueSheetStateListItems(data);

  return {
    general: {
      left: [
        listItems.data.spec.inquiryDate,
        listItems.data.spec.fromDate,
        listItems.data.spec.toDate,
        listItems.data.spec.cusip,
        listItems.data.spec.symbol,
        listItems.data.spec.sidePill,
        { ...listItems.data.spec.requester.code, title: 'Requester Code' },
        { ...listItems.data.spec.requester.code, title: 'Requester Organization Number' },
      ] satisfies ReadonlyListItem[],
      right: [listItems.metadata.createdAt, listItems.metadata.updatedAt, listItems.reportId] satisfies ReadonlyListItem[],
    },
  };
}

function getMenuItems(
  reportId: string,
  status: IxcTradereportingV1BlueSheetStatus | undefined,
  downloadReport: VoidFunction,
  isDownloading: boolean,
): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: TradeReportingBlueSheetEventDrawer,
        props: { request: { reportId } },
      },
    },
    actions:
      status === IxcTradereportingV1BlueSheetStatus.Reported
        ? [
            {
              id: 'download',
              icon: <DownloadIcon aria-hidden />,
              label: 'Download',
              disabled: isDownloading,
              onClick: downloadReport,
            },
          ]
        : [],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load blue sheet report', 'view this blue sheet report');
const downloadErrorMessageGetter = buildDefaultErrorMessageGetter('Failed to download report', 'download this report');
function BlueSheetReportDetail() {
  const { reportId } = Route.useParams();
  const request = { reportId } as const;
  const { data, error, isPending } = useIxcTradereportingV1BlueSheetQueryServiceBlueSheetGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const { mutateAsync: downloadReport, error: downloadError, isPending: isDownloading } = useIxfReportsV1ReportCommandServiceReportDownload();
  useErrorHandler(downloadError, downloadErrorMessageGetter);
  const handleDownloadReport = useCallback(async () => {
    try {
      const res = await downloadReport({ reportId });

      if (res?.url) {
        downloadFromUrl(res.url);
      }
    } catch {}
  }, [downloadReport, reportId]);

  const listItems = useMemo(() => getListItems(data?.blueSheet), [data?.blueSheet]);
  const menuItems = useMemo(
    () => getMenuItems(reportId, data?.blueSheet?.status, handleDownloadReport, isDownloading),
    [reportId, data?.blueSheet?.status, handleDownloadReport, isDownloading],
  );

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <TradeReportingBlueSheetStatusPill isPending={showPending} size="large" status={data?.blueSheet?.status} />
            <OrderSidePill isPending={showPending} side={data?.blueSheet?.data?.spec?.side} size="large" />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <TradeReportingBlueSheetEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
